/**
 * ioLiberum Outlook Add-in — Webhook Service Layer
 * All communications to N8N go through the Express backend proxy.
 * The webhook secret is injected server-side — never exposed to the client.
 */

import { getToken, logout } from './auth';

/**
 * Build the standard payload that every webhook expects.
 * @param {object} params
 * @returns {object}
 */
function buildPayload({ source, action, outlookItemId, conversationId, userEmail, senderEmail, matterId, matterNumber, matterDescription, params = {} }) {
    return {
        source,
        action,
        outlook_item_id: outlookItemId,
        conversation_id: conversationId,
        user_email: userEmail,
        sender_email: senderEmail,
        timestamp: new Date().toISOString(),
        matter_id: matterId || null,
        matter_number: matterNumber || null,
        matter_description: matterDescription || null,
        params,
    };
}

/**
 * Send a webhook request via the authenticated backend proxy.
 * @param {string} route — webhook path (e.g., '/webhook/file-email')
 * @param {object} payload — request body
 * @returns {Promise<object>} — parsed response
 */
async function sendWebhook(route, payload) {
    // Route through the Express proxy: /api/webhook/file-email
    const proxyRoute = route.startsWith('/webhook/')
        ? route.replace('/webhook/', '')
        : route.replace('/', '');

    const token = getToken();
    if (!token) {
        throw {
            status: 'error',
            code: 'AUTH_REQUIRED',
            message: 'Not authenticated — please log in',
            retryable: false,
        };
    }

    const response = await fetch(`/api/webhook/${proxyRoute}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
    });

    // If 401, token expired — force re-login
    if (response.status === 401) {
        logout();
        window.location.reload();
        throw {
            status: 'error',
            code: 'AUTH_EXPIRED',
            message: 'Session expired — please log in again',
            retryable: false,
        };
    }

    if (!response.ok) {
        let errorData;
        try {
            errorData = await response.json();
        } catch {
            errorData = { message: `HTTP ${response.status}: ${response.statusText}` };
        }
        throw {
            status: 'error',
            code: errorData.code || 'HTTP_ERROR',
            message: errorData.message || `Request failed with status ${response.status}`,
            retryable: response.status >= 500,
        };
    }

    return response.json();
}

// ═══════════════════════════════════
// Public API — one function per webhook route
// ═══════════════════════════════════

/**
 * Look up the matter for the current email.
 * @param {string|null} subjectMatterId - Matter ID extracted from the email subject line [id], if present.
 *   When provided, N8N can fast-path the lookup by querying Clio directly instead of fuzzy matching.
 */
export async function lookupMatter({ outlookItemId, conversationId, userEmail, senderEmail, surface, subjectMatterId }) {
    return sendWebhook('/webhook/lookup-matter', buildPayload({
        source: surface || 'reading_pane',
        action: 'lookup_matter',
        outlookItemId,
        conversationId,
        userEmail,
        params: {
            sender_email: senderEmail,
            subject_matter_id: subjectMatterId || null,
        },
    }));
}

/**
 * Get full matter details, custom fields, relationships, and financials.
 */
export async function getMatterDetails({ userEmail, matterId }) {
    return sendWebhook('/webhook/matter-details', buildPayload({
        source: 'reading_pane',
        action: 'get_matter_details',
        userEmail,
        matterId
    }));
}

/**
 * Link a matter to the current conversation.
 * @param {string} source - the surface initiating the link ('reading_pane' | 'compose')
 * @param {string} linkSource - how the link was created ('manual' | 'auto' | etc.)
 */
export async function linkMatter({ outlookItemId, conversationId, userEmail, senderEmail, matterId, matterNumber, matterDescription, source, linkSource }) {
    return sendWebhook('/webhook/link-matter', buildPayload({
        source: source || 'reading_pane',
        action: 'link_matter',
        outlookItemId,
        conversationId,
        userEmail,
        senderEmail,
        matterId,
        matterNumber,
        matterDescription,
        params: { link_source: linkSource || 'manual' },
    }));
}

/**
 * File email to Clio.
 */
export async function fileEmail({ source, outlookItemIds, conversationId, userEmail, matterId, matterNumber, matterDescription, clientName, contactId, extractAttachments, extractImages }) {
    return sendWebhook('/webhook/file-email-v3', buildPayload({
        source,
        action: 'file_email',
        outlookItemId: outlookItemIds[0],
        conversationId,
        userEmail,
        matterId,
        matterNumber,
        matterDescription,
        params: {
            outlook_item_ids: outlookItemIds,
            client_name: clientName,
            contact_id: contactId,
            extract_attachments: extractAttachments,
            extract_images: extractImages
        }
    }));
}

/**
 * Mark email for filing.
 */
export async function markForFiling({ outlookItemId, conversationId, userEmail, senderEmail, matterId, matterNumber, matterDescription, source }) {
    return sendWebhook('/webhook/mark-for-filing', buildPayload({
        source,
        action: 'mark_for_filing',
        outlookItemId,
        conversationId,
        userEmail,
        senderEmail,
        matterId,
        matterNumber,
        matterDescription,
    }));
}


/**
 * Fetch all emails in a conversation.
 */
export async function getConversationEmails({ conversationId, userEmail }) {
    return sendWebhook('/webhook/conversation-emails', buildPayload({
        source: 'actions_pane',
        action: 'get_conversation_emails',
        outlookItemId: null,
        conversationId,
        userEmail,
    }));
}

/**
 * Create task(s) in Clio.
 */
export async function createTask({ outlookItemId, conversationId, userEmail, matterId, matterNumber, tasks }) {
    return sendWebhook('/webhook/create-task', buildPayload({
        source: 'reading_pane',
        action: 'create_task',
        outlookItemId,
        conversationId,
        userEmail,
        matterId,
        matterNumber,
        params: { tasks },
    }));
}

/**
 * Perform CRUD operations on tasks for a matter.
 */
export async function matterTasks({ userEmail, matterId, taskAction, taskId, etag, taskData }) {
    return sendWebhook('/webhook/matter-tasks', buildPayload({
        source: 'reading_pane',
        action: 'matter_tasks',
        userEmail,
        matterId,
        params: { task_action: taskAction, task_id: taskId, etag, task_data: taskData },
    }));
}

/**
 * Fetch documents for a matter.
 * @param {'recent'|'tree'} view - 'recent' returns a flat list of the last 25 docs sorted by
 *   updated_at (response shape: { documents: [...] }). 'tree' returns the full folder tree
 *   (response shape: { tree: [...] }). Defaults to 'recent'.
 */
export async function matterDocuments({ userEmail, matterId, view = 'recent' }) {
    return sendWebhook('/webhook/matter-documents', buildPayload({
        source: 'reading_pane',
        action: 'matter_documents',
        userEmail,
        matterId,
        params: { view },
    }));
}

/**
 * Fetch expansive history (communications, activities) for a matter.
 */
export async function matterHistory({ userEmail, matterId }) {
    return sendWebhook('/webhook/matter-history', buildPayload({
        source: 'reading_pane',
        action: 'matter_history',
        userEmail,
        matterId,
    }));
}

/**
 * Request an AI draft reply
 */
export async function draftReply({
    userEmail, matterId, matterNumber, conversationId, itemId,
    tone, model, modelProvider, modelApiId,
    context_included, thread_limit, matter_notes, recent_activity, prior_activity,
    general_instructions, tone_instructions, additional_instructions,
    attachments
}) {
    console.log(`[Webhook] Requesting draft reply for matter: ${matterId} | model: ${modelProvider}/${modelApiId}`);
    return await sendWebhook('/webhook/draft-reply', buildPayload({
        source: 'actions_pane',
        action: 'draft_reply',
        outlookItemId: itemId,
        conversationId,
        userEmail,
        matterId,
        matterNumber,
        params: {
            model, model_provider: modelProvider, model_api_id: modelApiId, tone,
            context_included, thread_limit, matter_notes, recent_activity, prior_activity,
            general_instructions, tone_instructions, additional_instructions,
            attachments
        },
    }));
}

/**
 * Fetch notes for a matter.
 */
export async function getMatterNotes({ userEmail, matterId }) {
    return sendWebhook('/webhook/matter-notes', buildPayload({
        source: 'actions_pane',
        action: 'get_matter_notes',
        userEmail,
        matterId,
    }));
}

/**
 * Log a time entry to Clio.
 */
export async function logTimeEntry({ outlookItemId, conversationId, userEmail, matterId, matterNumber, duration, description, date }) {
    return sendWebhook('/webhook/log-time', buildPayload({
        source: 'reading_pane',
        action: 'log_time_entry',
        outlookItemId,
        conversationId,
        userEmail,
        matterId,
        matterNumber,
        params: { duration, description, date },
    }));
}

/**
 * Add a note to a matter in Clio.
 */
export async function addNote({ outlookItemId, conversationId, userEmail, matterId, matterNumber, noteText }) {
    return sendWebhook('/webhook/add-note', buildPayload({
        source: 'reading_pane',
        action: 'add_note',
        outlookItemId,
        conversationId,
        userEmail,
        matterId,
        matterNumber,
        params: { note_text: noteText },
    }));
}

/**
 * Summarize the email thread.
 */
export async function summarizeThread({ outlookItemId, conversationId, userEmail, matterId, matterNumber }) {
    return sendWebhook('/webhook/summarize', buildPayload({
        source: 'actions_pane',
        action: 'summarize_thread',
        outlookItemId,
        conversationId,
        userEmail,
        matterId,
        matterNumber,
    }));
}

/**
 * Triggers a manual refresh of the Clio contact/matter cache in superset_db.
 */
export async function refreshCache({ userEmail }) {
    return sendWebhook('/webhook/update-clio', buildPayload({
        source: 'actions_pane',
        action: 'refresh_cache',
        userEmail,
    }));
}


/**
 * Unlink a matter from the current conversation.
 */
export async function unlinkMatter({ conversationId, userEmail, surface }) {
    return sendWebhook('/webhook/unlink-matter', buildPayload({
        source: surface || 'reading_pane',
        action: 'unlink_matter',
        conversationId,
        userEmail,
    }));
}

/**
 * Search matters by free-text query (name, number, client, etc.).
 */
export async function searchMatters({ userEmail, query, surface }) {
    return sendWebhook('/webhook/search-matters', buildPayload({
        source: surface || 'reading_pane',
        action: 'search_matters',
        userEmail,
        params: { query },
    }));
}

/**
 * Search companies by free-text query.
 */
export async function searchCompanies({ userEmail, query }) {
    return sendWebhook('/webhook/search-companies', buildPayload({
        source: 'reading_pane',
        action: 'search_companies',
        userEmail,
        params: { query },
    }));
}

/**
 * Run a conflict check for a sender.
 */
export async function conflictCheck({ outlookItemId, conversationId, userEmail, senderEmail }) {
    return sendWebhook('/webhook/conflict-check', buildPayload({
        source: 'reading_pane',
        action: 'conflict_check',
        outlookItemId,
        conversationId,
        userEmail,
        params: { sender_email: senderEmail },
    }));
}

/**
 * Fetch a document from Clio for attachment.
 */
export async function attachFromClio({ outlookItemId, conversationId, userEmail, matterId, matterNumber, documentId }) {
    return sendWebhook('/webhook/attach-from-clio', buildPayload({
        source: 'compose',
        action: 'attach_from_clio',
        outlookItemId,
        conversationId,
        userEmail,
        matterId,
        matterNumber,
        params: { document_id: documentId },
    }));
}

/**
 * Fetch users from Clio for task assignment.
 */
export async function getUsers({ userEmail }) {
    return sendWebhook('/webhook/get-users', buildPayload({
        source: 'reading_pane',
        action: 'get_users',
        userEmail,
    }));
}

/**
 * Backup or restore settings to/from the cloud (N8N/Postgres).
 */
export async function syncSettings({ userEmail, action, settings }) {
    return sendWebhook('/webhook/sync-settings', buildPayload({
        source: 'settings_pane',
        action: `sync_settings_${action}`, // 'sync_settings_backup' or 'sync_settings_restore'
        userEmail,
        params: { sync_action: action, settings },
    }));
}

/**
 * Use AI (via N8N) to extract contact info from the current email.
 * Returns: { first_name, last_name, email, phone, company, title, type }
 */
export async function extractContactInfo({ userEmail, outlookItemId, conversationId }) {
    return sendWebhook('/webhook/extract-contact', buildPayload({
        source: 'reading_pane',
        action: 'extract_contact_info',
        outlookItemId,
        conversationId,
        userEmail,
    }));
}

/**
 * Check whether a contact with the given email already exists in Clio.
 * Returns: { exists: boolean, contact?: { id, name, email } }
 */
export async function checkContactEmail({ userEmail, email }) {
    return sendWebhook('/webhook/check-contact-email', buildPayload({
        source: 'reading_pane',
        action: 'check_contact_email',
        userEmail,
        params: { email },
    }));
}

/**
 * Create a new contact in Clio, link them to a matter, and associate the conversation.
 * @param {object} params
 * @param {string} params.userEmail
 * @param {string} params.outlookItemId
 * @param {string} params.conversationId
 * @param {object} params.contact - { type, first_name, last_name, email, phone, company, title }
 * @param {string|null} params.matterId
 * @param {string|null} params.matterNumber
 * @param {string|null} params.matterDescription
 * @param {string|null} params.relationshipDescription
 */
export async function addContact({ userEmail, outlookItemId, conversationId, contact, matterId, matterNumber, matterDescription, relationshipDescription }) {
    return sendWebhook('/webhook/add-contact', buildPayload({
        source: 'reading_pane',
        action: 'add_contact',
        outlookItemId,
        conversationId,
        userEmail,
        matterId,
        matterNumber,
        matterDescription,
        params: {
            contact,
            relationship_description: relationshipDescription || null,
        },
    }));
}

/**
 * Scan the top-level inbox for read conversations linked to matters.
 * Returns: { conversations: [{ conversationId, subject, sender, receivedDateTime, matter, items }] }
 */
export async function scanInbox({ userEmail }) {
    return sendWebhook('/webhook/scan-inbox', buildPayload({
        source: 'inbox_tab',
        action: 'scan_inbox',
        userEmail,
    }));
}

/**
 * Fetch contact details by ID.
 */
export async function getContact({ userEmail, contactId }) {
    return sendWebhook('/webhook/get-contact', buildPayload({
        source: 'reading_pane',
        action: 'get_contact',
        userEmail,
        params: { contact_id: contactId },
    }));
}

/**
 * Update contact details by ID.
 */
export async function updateContact({ userEmail, contactId, etag, contactData }) {
    return sendWebhook('/webhook/update-contact', buildPayload({
        source: 'reading_pane',
        action: 'update_contact',
        userEmail,
        params: { contact_id: contactId, etag, contact_data: contactData },
    }));
}

/**
 * Fetch all child folders under Inbox. Results are cached client-side in localStorage.
 * Returns: { status: 'success', folders: [{ id, displayName, totalItemCount, unreadItemCount }] }
 */
export async function getInboxFolders({ userEmail }) {
    return sendWebhook('/webhook/get-inbox-folders', buildPayload({
        source: 'triage',
        action: 'get_inbox_folders',
        userEmail,
    }));
}

/**
 * Fetch all inbox + sent item conversations for batch triage.
 * Returns: { conversations: [{ conversationId, subject, sender, date, threadCount, items, linkedMatter? }] }
 */
export async function triageInbox({ userEmail }) {
    return sendWebhook('/webhook/triage-inbox', buildPayload({
        source: 'triage',
        action: 'triage_inbox',
        userEmail,
    }));
}

/**
 * Move all emails in a conversation to a destination folder (e.g. 'archive').
 * Passing messageIds lets the workflow skip the secondary Graph search and process exact IDs.
 */
export async function moveEmails({ userEmail, conversationId, messageIds, destination }) {
    return sendWebhook('/webhook/move-emails', buildPayload({
        source: 'triage',
        action: 'move_emails',
        conversationId,
        userEmail,
        params: { destination, messageIds: messageIds || [] },
    }));
}

/**
 * Delete all emails in a conversation.
 * Passing messageIds lets the workflow skip the secondary Graph search and process exact IDs.
 */
export async function deleteEmails({ userEmail, conversationId, messageIds }) {
    return sendWebhook('/webhook/delete-emails', buildPayload({
        source: 'triage',
        action: 'delete_emails',
        conversationId,
        userEmail,
        params: { messageIds: messageIds || [] },
    }));
}
